/**
 * Plans — the planning service's resource, reached through the gateway.
 *
 * A plan is a RECORD the planning service owns: a care plan on a stay, a roster
 * on a ward. Its shape is generic and the same for every category; what differs
 * is `attributes` and the shape of an `item`, both declared by the category
 * in the planning service. Nothing in this module knows a category's field
 * names, which is why `attributes` and `items` are generic here and narrowed by
 * the caller that knows which category it opened.
 *
 * ONE WRITE, THE WHOLE LIST. `PUT /plans/{category}/{id}` replaces a plan's items and
 * requires `If-Match`. There are no per-item verbs: the service compares what
 * arrives with what it holds, matches on each item's `id`, and records one
 * event per item that actually changed. A caller that wants to move one item
 * therefore sends the whole list with that item moved — which is what a board
 * holds anyway — and two boards editing different items no longer overwrite
 * each other, because the second read carries the first's change.
 *
 * THE VERSION TRAVELS IN THE BODY. The service sends both `ETag: "3"` and
 * `{"version": 3}`, and they are the same number. Reading one of them keeps the
 * version with one home on this side too. It rides back out as `If-Match: "3"`.
 *
 * THE STATUS IS THE LIFECYCLE, and it is not written by `PUT`. A plan is
 * `draft` when created, `active` from its first publish, and `completed` or
 * `revoked` at the end; each transition is its own custom method. A replace
 * after a publish is a NEW PROPOSAL and the published version stays in force
 * until `:publish` runs again, so a board that replaced items and did not
 * publish has changed nothing the ward acts on.
 */

import type { Page } from '../types';
import { api } from '../client';
import { createLogger } from '@/utils/logger';
import { randomUUID } from '@/lib/uuid';

const logger = createLogger('PlansAPI');

/** The lifecycle, name for name with FHIR's request-status. */
export type PlanStatus = 'draft' | 'active' | 'completed' | 'revoked';

/**
 * The two statuses a plan is still in play in.
 *
 * `completed` and `revoked` are final, so a surface that opens on "the current
 * plan" asks the service for these two and takes the first answer. It is one
 * filter and not a rule applied afterwards: the service orders newest first,
 * and a page filtered on the way out is shorter than a page filtered here.
 */
export const OPEN_PLAN_STATUSES: readonly PlanStatus[] = ['draft', 'active'];

/** What a plan is about. `type` is one of the category's declared subject types. */
export interface PlanSubject {
  type: string;
  id: string;
}

/**
 * One plan.
 *
 * `attributes` and each item are the CATEGORY's shapes, so they are objects
 * here and narrowed where the category is known. A record type rather than
 * `unknown` because every one of them is a JSON object by the declaration's own
 * schema, and a caller that has read the category may index it.
 */
export interface Plan {
  id: string;
  subject: PlanSubject;
  category: string;
  status: PlanStatus;
  version: number;
  attributes: Record<string, unknown>;
  items: PlanItem[];
}

/** Every item carries an id, in every category. It is how an item keeps its identity across writes. */
export interface PlanItem extends Record<string, unknown> {
  id: string;
}

/** The shared list envelope. Empty is an answer. */
export type PlanListResponse = Page<Plan>;

/**
 * A plan's address. The category is part of it on every surface, because a plan
 * lives in its category's collection and never moves between them.
 *
 * A `Plan` IS a `PlanRef`, so a caller holding one passes it straight in rather
 * than taking its two halves apart and putting them back together.
 */
export interface PlanRef {
  category: string;
  id: string;
}

function planPath(ref: PlanRef): string {
  return `/plans/${encodeURIComponent(ref.category)}/${encodeURIComponent(ref.id)}`;
}

/**
 * The plans on one subject, in one category, in any of the given statuses.
 *
 * A subject may hold several plans of a category — last week's roster and this
 * week's — so this answers a list and never a single plan. An empty list means
 * nobody has planned yet, which is a reason to create one.
 *
 * NEWEST FIRST, ALWAYS, whatever the filter: the service orders by creation
 * time descending on every surface. A caller wanting the plan a board opens on
 * therefore reads `items[0]`, and nothing here sorts or scans.
 */
export async function listPlans(
  subject: PlanSubject,
  category: string,
  statuses: readonly PlanStatus[],
): Promise<PlanListResponse> {
  const query = new URLSearchParams({ subject: `${subject.type}/${subject.id}` });
  // Repeated, because "the ones still open" is one question: `?status=draft&status=active`.
  for (const status of statuses) query.append('status', status);
  try {
    // The category is the COLLECTION, not a filter: it is in the path, and
    // there is no list across categories to ask for.
    return await api.getPage<PlanListResponse>(
      `/plans/${encodeURIComponent(category)}?${query.toString()}`,
    );
  } catch (error) {
    logger.error('Failed to list plans', { subject, category, error });
    throw error;
  }
}

/** One plan, at its current version. */
export async function getPlan(ref: PlanRef): Promise<Plan> {
  try {
    return await api.get<Plan>(planPath(ref));
  } catch (error) {
    logger.error('Failed to read plan', { ref, error });
    throw error;
  }
}

/**
 * Create a plan on a subject.
 *
 * The id is derived from the idempotency key by the service, so a create sent
 * twice names one plan rather than two.
 */
export async function createPlan(input: {
  subject: PlanSubject;
  category: string;
  attributes: Record<string, unknown>;
  idempotencyKey?: string;
}): Promise<Plan> {
  try {
    // The body carries no category: the collection posted to is the category,
    // so the request states it once.
    return await api.post<Plan>(
      `/plans/${encodeURIComponent(input.category)}`,
      { subject: input.subject, attributes: input.attributes },
      { headers: { 'Idempotency-Key': input.idempotencyKey ?? randomUUID() } },
    );
  } catch (error) {
    logger.error('Failed to create plan', { category: input.category, error });
    throw error;
  }
}

/**
 * Replace a plan's items, against the version they were decided on.
 *
 * A replace that changes nothing appends nothing and answers the plan at the
 * version it already had, so a save the nurse did not actually change is not a
 * new version every other reader has to catch up with.
 */
export async function replacePlanItems(
  ref: PlanRef,
  version: number,
  items: PlanItem[],
  idempotencyKey?: string,
): Promise<Plan> {
  try {
    const plan = await api.put<Plan>(planPath(ref), { items }, {
      headers: {
        'If-Match': `"${version}"`,
        'Idempotency-Key': idempotencyKey ?? randomUUID(),
      },
    });
    logger.info('Plan items replaced', { ref, version: plan.version, items: plan.items.length });
    return plan;
  } catch (error) {
    logger.error('Failed to replace plan items', { ref, version, error });
    throw error;
  }
}

/** The four processes. Each answers the plan the process left behind. */
export type PlanMethod = 'solve' | 'publish' | 'complete' | 'revoke';

async function planMethod(
  ref: PlanRef,
  method: PlanMethod,
  version: number,
  idempotencyKey?: string,
): Promise<Plan> {
  try {
    const plan = await api.post<Plan>(`${planPath(ref)}:${method}`, undefined, {
      headers: {
        'If-Match': `"${version}"`,
        'Idempotency-Key': idempotencyKey ?? randomUUID(),
      },
    });
    logger.info('Plan method applied', { ref, method, version: plan.version, status: plan.status });
    return plan;
  } catch (error) {
    logger.error('Plan method failed', { ref, method, version, error });
    throw error;
  }
}

/** Make this version the one in force. */
export function publishPlan(ref: PlanRef, version: number, idempotencyKey?: string): Promise<Plan> {
  return planMethod(ref, 'publish', version, idempotencyKey);
}

/** Record that a plan in force has run its course. */
export function completePlan(ref: PlanRef, version: number, idempotencyKey?: string): Promise<Plan> {
  return planMethod(ref, 'complete', version, idempotencyKey);
}

/** Withdraw a plan. It is not how a plan is corrected — a correction is a replace. */
export function revokePlan(ref: PlanRef, version: number, idempotencyKey?: string): Promise<Plan> {
  return planMethod(ref, 'revoke', version, idempotencyKey);
}

/**
 * Occurrences — execution, which the planning service owns for every plan.
 *
 * An occurrence is ONE ITEM OF THE VERSION IN FORCE ON ONE DATE. A shift is the
 * occurrence of a roster item; a care activity is the occurrence of a care item.
 * Nothing stores a row per occurrence on either side: its status is folded from
 * the plan's change log, and one nobody has acted on is `scheduled` with nothing
 * written for it.
 *
 * THE DISPLAY ARRIVES RESOLVED. The label, colour and window come from the
 * category's declaration, resolved by the planning service. This is the ONLY way
 * a client learns them: a category's declaration is tenant policy and is not
 * served over the API, so a board draws what this read answers and assembles
 * nothing itself.
 *
 * Occurrences exist only for the version in force and only while the plan is
 * active. A draft never published has none, and the read answers an empty page
 * rather than an error.
 */

/** Where execution stands. The last three are final. */
export type OccurrenceStatus = 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'missed';

/** What a surface shows for an occurrence, from the category's declaration. */
export interface OccurrenceDisplay {
  /** The name per locale, keyed by locale code. A surface picks the viewer's. */
  label: Record<string, string>;
  /** An opaque token this app's own token set renders. NOT a CSS value. */
  colour: string;
  /** `HH:MM`, when the declaration states a window. */
  start?: string;
  end?: string;
}

/** One item of the version in force on one date. */
export interface Occurrence {
  item_id: string;
  /** `YYYY-MM-DD`. */
  date: string;
  status: OccurrenceStatus;
  /** RFC 3339. When the item is planned to start and end on this date. */
  planned_start: string;
  planned_end: string;
  /** Absent when the category declares none — draw the item without one. */
  display?: OccurrenceDisplay;
}

/** The window is the whole answer, so the response carries no `rel="next"`. */
export type OccurrenceListResponse = Page<Occurrence>;

/**
 * Every occurrence of the version in force between two dates, inclusive.
 *
 * Both dates are required. This is what a board reads to render a week: one
 * call answers every day in the window, already carrying what to draw.
 */
export async function listOccurrences(
  ref: PlanRef,
  from: string,
  to: string,
): Promise<OccurrenceListResponse> {
  const query = new URLSearchParams({ from, to });
  try {
    return await api.getPage<OccurrenceListResponse>(
      `${planPath(ref)}/occurrences?${query.toString()}`,
    );
  } catch (error) {
    logger.error('Failed to list occurrences', { ref, from, to, error });
    throw error;
  }
}

/** The four processes on an occurrence. Each answers the occurrence it left. */
export type OccurrenceMethod = 'start' | 'complete' | 'cancel' | 'miss';

/** One occurrence's address: its plan, its item and the date it occurs on. */
export interface OccurrenceRef extends PlanRef {
  itemId: string;
  /** `YYYY-MM-DD`. */
  date: string;
}

function occurrencePath(ref: OccurrenceRef): string {
  return `${planPath(ref)}/items/${encodeURIComponent(ref.itemId)}/occurrences/${encodeURIComponent(ref.date)}`;
}

/**
 * Apply one process to one occurrence.
 *
 * NO `If-Match`. A process acts on the occurrence as the plan stands, which is
 * what the planning service does with an unset version. A board sends no
 * precondition because it is not proposing a change to the plan's content: it
 * is recording that something happened, and the plan's version moving under it
 * — someone else starting another shift — must not turn that into a refusal the
 * nurse has to resolve.
 *
 * The idempotency key is what makes a retried tap one event rather than two.
 */
async function occurrenceMethod(
  ref: OccurrenceRef,
  method: OccurrenceMethod,
  idempotencyKey?: string,
): Promise<Occurrence> {
  try {
    const occurrence = await api.post<Occurrence>(`${occurrencePath(ref)}:${method}`, undefined, {
      headers: { 'Idempotency-Key': idempotencyKey ?? randomUUID() },
    });
    logger.info('Occurrence method applied', { ref, method, status: occurrence.status });
    return occurrence;
  } catch (error) {
    logger.error('Occurrence method failed', { ref, method, error });
    throw error;
  }
}

/** Record that this occurrence started. Starting a shift is this. */
export function startOccurrence(ref: OccurrenceRef, idempotencyKey?: string): Promise<Occurrence> {
  return occurrenceMethod(ref, 'start', idempotencyKey);
}

/** Record that this occurrence finished. Final. */
export function completeOccurrence(ref: OccurrenceRef, idempotencyKey?: string): Promise<Occurrence> {
  return occurrenceMethod(ref, 'complete', idempotencyKey);
}

/** Record that this occurrence was called off before it began. Final. */
export function cancelOccurrence(ref: OccurrenceRef, idempotencyKey?: string): Promise<Occurrence> {
  return occurrenceMethod(ref, 'cancel', idempotencyKey);
}

/**
 * Record that this occurrence should have happened and did not. Final.
 *
 * Distinct from cancelling: cancelled says it was withdrawn beforehand, missed
 * says it was still expected. A board must not offer one for the other.
 */
export function missOccurrence(ref: OccurrenceRef, idempotencyKey?: string): Promise<Occurrence> {
  return occurrenceMethod(ref, 'miss', idempotencyKey);
}
